import { useState } from "react";
import { Button } from "@nosible/shared/components/ui/button";
import { Input } from "@nosible/shared/components/ui/input";
import { Label } from "@nosible/shared/components/ui/label";
import { Edit2, Check, X } from "lucide-react";
import { trpc } from "@/account/lib/trpc";

interface EditUserNameProps {
  currentName: string | null;
  userId: string;
  onUpdate: () => void;
}

export function EditUserName({ currentName, userId, onUpdate }: EditUserNameProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(currentName || "");

  const editName = trpc.user.editName.useMutation({
    onSuccess: () => {
      setIsEditing(false);
      onUpdate();
    },
  });

  const handleSave = () => {
    if (name.trim()) {
      editName.mutate({ name: name.trim() });
    }
  };

  const handleCancel = () => {
    setName(currentName || "");
    setIsEditing(false);
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  if (isEditing) {
    return (
      <div className="space-y-2">
        <Label htmlFor="name" className="text-card-foreground text-sm">
          Name
        </Label>
        <div className="flex items-center gap-2">
          <Input
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="flex-1"
            disabled={editName.isPending}
          />
          <Button
            size="sm"
            type="button"
            onClick={handleSave}
            disabled={editName.isPending || !name.trim()}
          >
            <Check className="h-4 w-4" />
            Save
          </Button>
          <Button
            size="sm"
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={editName.isPending}
          >
            <X className="h-4 w-4" />
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between">
      <div>
        <Label className="text-card-foreground text-sm">Name</Label>
        <p className="text-silica">{currentName || "—"}</p>
      </div>
      <Button size="sm" type="button" variant="ghost" onClick={handleEdit}>
        <Edit2 className="h-4 w-4" />
        Edit
      </Button>
    </div>
  );
}
